import { clsx } from 'clsx';
import { ChevronDown, Check } from 'lucide-react';

interface SectionAccordionProps {
  sectionId: string;
  name: string;
  weight: number;
  completedCount: number;
  totalCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export function SectionAccordion({
  sectionId,
  name,
  weight,
  completedCount,
  totalCount,
  isExpanded,
  onToggle,
  children,
}: SectionAccordionProps) {
  const isComplete = completedCount === totalCount;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="border border-wolf-700/50 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className={clsx(
          'w-full flex items-center justify-between p-4 text-left transition-colors',
          isExpanded ? 'bg-wolf-800/50' : 'bg-wolf-900/30 hover:bg-wolf-800/30'
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
              isComplete
                ? 'bg-action text-navy-dark'
                : 'bg-wolf-700 text-wolf-300'
            )}
          >
            {isComplete ? <Check className="w-4 h-4" /> : sectionId}
          </div>
          <div>
            <span className="text-wolf-100 font-medium">{name}</span>
            <span className="text-wolf-500 text-sm ml-2">
              ({(weight * 100).toFixed(0)}%)
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span
            className={clsx(
              'text-sm',
              isComplete ? 'text-action' : 'text-wolf-400'
            )}
          >
            {completedCount}/{totalCount}
          </span>
          <ChevronDown
            className={clsx(
              'w-5 h-5 text-wolf-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Progress bar */}
      <div className="h-1 bg-wolf-800">
        <div
          className="h-full bg-action transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-wolf-900/20">{children}</div>
      )}
    </div>
  );
}
